import GoogleTaskMonitoring from './GoogleTaskMonitoring';
import ConnectionOverview from './ConnectionOverview';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { googleIntegrationsService } from '../../services/api';
import { newRequestId } from '../../utils/requestId';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  Squares2X2Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const getApiOrigin = () => {
  try {
    return new URL(process.env.REACT_APP_API_URL || 'http://localhost:8000/api').origin;
  } catch {
    return null;
  }
};

const GOOGLE_SERVICES = [
  ['calendar', 'Calendar', 'Read calendars and manage meetings after review.'],
  ['gmail', 'Gmail', 'Read mail and send reviewed messages.'],
  ['drive', 'Drive', 'Find files, read selected content and create reviewed attachments.'],
  ['docs', 'Docs', 'Read selected documents and append reviewed notes.'],
  ['sheets', 'Sheets', 'Track tasks and bugs, and apply reviewed row updates.'],
  ['tasks', 'Tasks', 'Import task notes and review task changes.'],
];
const hasCalendarAccess = status => Boolean((status?.calendar_access ?? status?.google_calendar_connected) && !status?.reconnect_required);
const permissionLabel = (id, status) => {
  if (!status) return 'Status not loaded';
  if (status.reconnect_required) return 'Reconnect needed';
  const saved = status.services_status?.[id];
  if (id === 'gmail' && saved !== Boolean(status.gmail_send) && (saved || status.gmail_send)) {
    return saved ? 'Read permission saved; sending permission needed' : 'Sending permission saved; read permission needed';
  }
  return saved ? 'Permission saved' : 'Permission not saved';
};

const GoogleIntegration = () => {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [calendarId, setCalendarId] = useState('primary');
  const [includeNotes, setIncludeNotes] = useState(true);
  const [exportEvents, setExportEvents] = useState(false);
  const [exportNotes, setExportNotes] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [overviewVersion, setOverviewVersion] = useState(0);
  const [selectedServices, setSelectedServices] = useState(['calendar']);
  const [connectionError, setConnectionError] = useState('');
  const popupRef = useRef(null);
  const popupCheckRef = useRef(null);

  const loadData = useCallback(async () => {
    const [connectionResult, syncResult, conflictsResult] = await Promise.allSettled([
      googleIntegrationsService.getStatus(),
      googleIntegrationsService.getSyncStatus(),
      googleIntegrationsService.getSyncConflicts(),
    ]);
    setConnectionStatus(connectionResult.status === 'fulfilled' ? connectionResult.value : null);
    setStatusError(connectionResult.status !== 'fulfilled' || syncResult.status !== 'fulfilled');
    setOverviewVersion(value => value + 1);
    if (syncResult.status === 'fulfilled') setSyncStatus(syncResult.value);
    if (conflictsResult.status === 'fulfilled') setConflicts(conflictsResult.value.conflicts || []);
    if (connectionResult.status === 'fulfilled' && hasCalendarAccess(connectionResult.value)) {
      try { setCalendars((await googleIntegrationsService.getCalendars()).calendars || []); }
      catch { setCalendars([]); }
    } else setCalendars([]);
  }, []);

  useEffect(() => {
    loadData();
    return () => window.clearInterval(popupCheckRef.current);
  }, [loadData]);

  useEffect(() => {
    const apiOrigin = getApiOrigin();
    const handleGoogleConnection = async (event) => {
      if (apiOrigin && event.origin !== apiOrigin) return;
      if (!popupRef.current || event.source !== popupRef.current) return;
      if (!['timelyzer:google-connected', 'timelyzer:google-error'].includes(event.data?.type)) return;

      setIsConnecting(false);
      window.clearInterval(popupCheckRef.current);
      popupRef.current = null;
      if (event.data.type === 'timelyzer:google-connected') {
        setConnectionError('');
        toast.success('Google connection updated');
        await loadData();
      } else {
        const message = event.data.message || 'Google connection was not completed';
        setConnectionError(message);
        toast.error(message);
      }
    };

    window.addEventListener('message', handleGoogleConnection);
    return () => window.removeEventListener('message', handleGoogleConnection);
  }, [loadData]);

  const connected = hasCalendarAccess(connectionStatus);
  const accountSaved = Boolean(connectionStatus?.google_connected ?? connectionStatus?.google_calendar_connected);
  const tasksConnected = Boolean((connectionStatus?.services_status?.tasks ?? connectionStatus?.services_status?.keep) && !connectionStatus?.reconnect_required);

  const run = syncStatus?.latest_run;
  const syncPending = isSyncing || ['queued', 'running'].includes(run?.status);
  useEffect(() => {
    if (!['queued', 'running'].includes(run?.status)) return undefined;
    const interval = window.setInterval(loadData, 2500);
    return () => window.clearInterval(interval);
  }, [run?.status, loadData]);

  const connect = async (mailboxManagement = false, services = selectedServices) => {
    setConnectionError('');
    setIsConnecting(true);
    // Open during the user gesture so an awaited API request cannot trigger popup blocking.
    const popup = window.open('', 'google-auth', 'width=520,height=680,scrollbars=yes,resizable=yes');
    popupRef.current = popup;
    try {
      if (!popup) throw new Error('Allow popups to connect Google, then try again.');
      const authData = await googleIntegrationsService.getAuthUrl(mailboxManagement, services);
      popup.location.replace(authData.auth_url);
      popupCheckRef.current = window.setInterval(async () => {
        if (popup.closed) {
          window.clearInterval(popupCheckRef.current);
          popupRef.current = null;
          await loadData();
          setIsConnecting(false);
        }
      }, 800);
    } catch (error) {
      popup?.close();
      popupRef.current = null;
      setIsConnecting(false);
      const message = typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.message || 'Could not start Google connection';
      setConnectionError(message);
      toast.error(message);
    }
  };

  const sync = async () => {
    setIsSyncing(true);
    try {
      const response = await googleIntegrationsService.bidirectionalSync('prefer_latest', { calendar_id: calendarId, include_notes: tasksConnected && includeNotes, export_events: exportEvents, export_notes: tasksConnected && includeNotes && exportNotes, request_id: newRequestId() });
      if (response.run) setSyncStatus(current => ({...current, latest_run: response.run}));
      toast.success(response.message || 'Google sync queued');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not queue Google sync. Check its status before retrying.');
      await loadData();
    } finally {
      setIsSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect all Google services from Timelyzer? Saved records will remain available.')) return;
    try {
      await googleIntegrationsService.disconnect();
      setConnectionStatus(null);
      setSyncStatus(null);
      await loadData();
      toast.success('Google services disconnected');
    } catch (error) {
      toast.error('Could not disconnect Google services');
    }
  };

  const resolveConflict = async (conflict, resolution) => {
    try {
      await googleIntegrationsService.resolveSyncConflict(conflict.type, conflict.id, resolution, conflict.revision);
      toast.success('Sync conflict resolved');
      await loadData();
    } catch (error) {
      toast.error('Could not resolve this conflict');
    }
  };

  return (
    <div className="app-page">
      <div className="app-page-inner max-w-6xl">
        <header className="page-header">
          <div>
            <div className="eyebrow">
              <Squares2X2Icon className="h-4 w-4" />
              Connected services
            </div>
            <h1 className="page-title">Connections</h1>
            <p className="page-description">
              Check what Timelyzer can access, finish setup, and review source synchronization.
            </p>
          </div>
          {connected && (
            <button onClick={sync} disabled={syncPending} className="primary-button disabled:opacity-60">
              <ArrowPathIcon className={`h-4 w-4 ${syncPending ? 'animate-spin' : ''}`} />
              {syncPending ? 'Syncing' : 'Sync now'}
            </button>
          )}
        </header>

        <section className="surface-panel mb-5 flex flex-wrap items-center justify-between gap-3 p-5">
          <div><h2 className="font-bold">Work and life sources</h2><p className="mt-1 text-sm text-[#68746d]">Connect selected mail, documents, task sheets, Notion pages and X posts in your assistant.</p></div>
          <Link className="secondary-button" to="/assistant?view=connections">Open assistant connections</Link>
          <Link className="secondary-button" to="/documents">Browse documents</Link>
          <Link className="secondary-button" to="/social">Browse X sources</Link>
          <Link className="secondary-button" to="/mail">Browse Gmail</Link>
        </section>

        <section id="google-settings" className="surface-panel mb-5 space-y-4 p-5" aria-label="Google permissions">
          <div><h2 className="section-heading">Google permissions</h2><p className="mt-2 text-sm text-[#68746d]">Choose the services you want to add. Use the same Google account to extend access; choosing another account replaces this connection. Previously granted permissions can remain available even when unchecked here.</p></div>
          <fieldset disabled={isConnecting}>
            <legend className="mb-3 text-sm font-semibold">Services to connect</legend>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {GOOGLE_SERVICES.map(([id, name, description]) => <label key={id} className="flex items-start gap-3 rounded-lg border border-[#e2e7e3] p-3">
                <input type="checkbox" className="mt-1" checked={selectedServices.includes(id)} onChange={event => setSelectedServices(current => event.target.checked ? [...current, id] : current.filter(item => item !== id))} />
                <span><span className="block text-sm font-bold">{name}</span><span className="mt-1 block text-xs text-[#68746d]">{description}</span><span className="mt-2 block text-xs font-semibold">{permissionLabel(id, connectionStatus)}</span></span>
              </label>)}
            </div>
          </fieldset>
          <p className="text-xs text-[#68746d]">Google may let you decline individual permissions. Saved access is shown separately for each service; it does not confirm the provider is currently reachable.</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => connect()} disabled={isConnecting || !selectedServices.length} className="primary-button disabled:opacity-60">
              <LinkIcon className="h-4 w-4" />{isConnecting ? 'Connecting…' : connectionStatus?.reconnect_required ? 'Reconnect Google' : accountSaved ? 'Update Google permissions' : 'Connect selected services'}
            </button>
            {accountSaved && <button onClick={disconnect} disabled={isConnecting} className="secondary-button"><XMarkIcon className="h-4 w-4" />Disconnect Google services</button>}
          </div>
          {connectionError && <p role="alert" className="text-sm text-[#996617]">{connectionError}</p>}
          {connectionStatus?.reconnect_required && <p role="alert" className="text-sm text-[#996617]">Google needs to be reconnected before calendars, email or documents can sync. Your saved records remain available.</p>}
        </section>

        <ConnectionOverview refreshKey={overviewVersion} />
        <GoogleTaskMonitoring connected={tasksConnected} onRefresh={loadData} />
        {statusError && <p role="alert" className="mb-4 text-sm text-[#996617]">Connection or sync status could not be loaded. <button className="underline" onClick={loadData}>Retry status check</button></p>}
        {run && <section className="surface-panel mb-5 space-y-3 p-5" aria-label="Google sync progress" aria-live="polite">
          <div className="flex items-center justify-between gap-3"><h2 className="font-bold">Latest Google sync</h2><span className="status-pill">{({queued:'Queued', running:'Running', completed:'Completed', partial:'Needs review', failed:'Failed', interrupted:'Interrupted'})[run.status] || run.status}</span></div>
          <p className="text-sm text-[#68746d]">{run.finished_at ? `Finished ${new Date(run.finished_at).toLocaleString()}` : 'Completion will appear here when the background job finishes. You can return to this page later.'}</p>
          {run.error && <p role="alert" className="text-sm text-[#996617]">{run.error}</p>}
          {Object.entries(run.result || {}).map(([phase, result]) => <div key={phase} className="rounded-lg border border-[#e2e7e3] p-3 text-sm">
            <p className="font-semibold">{phase === 'calendar' ? 'Calendar' : phase === 'task_text' ? 'Google Task text update' : phase === 'task_state' ? 'Google Task status and date update' : 'Google Tasks notes'}</p>
            {result.message && <p className="mt-1">{result.message}</p>}
            <p className="mt-1 text-[#68746d]">Imported: {result.imported ?? result.imported_from_google ?? 0} · {phase === 'calendar' ? `Refreshed: ${result.refreshed ?? 0}` : `Updated: ${result.updated ?? 0}`} · Exported: {result.exported ?? result.exported_to_google ?? 0} · Reconciled: {result.reconciled ?? 0}</p>
            {(result.errors || []).map((error, index) => <p key={index} className="mt-2 text-[#996617]">{typeof error === 'string' ? error : error.message}{error.note_id && <> <Link className="underline" to={`/notes?note=${error.note_id}`}>Open note</Link></>}{error.event_id && <> <Link className="underline" to={`/calendar/events/${error.event_id}`}>Open meeting</Link></>}</p>)}
          </div>)}
          {run.status === 'partial' && <p className="text-sm text-[#68746d]">Review conflicts below. Unverified exports are checked against saved receipts on the next sync and are not automatically resent.</p>}
        </section>}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-4">
            <section className="surface-panel overflow-hidden">
              <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#e2edf6] text-[#32699a]">
                    <CalendarDaysIcon className="h-6 w-6" />
                  </span>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-bold">Google Calendar</h2>
                      <span className={`status-pill ${connected ? 'status-pill-success' : 'status-pill-warning'}`}>
                        {connected ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
                        {connected ? 'Authorization saved' : 'Action needed'}
                      </span>
                    </div>
                    <p className="mt-2 max-w-xl text-sm leading-6 text-[#68746d]">
                      Import calendar events, keep Timelyzer changes synchronized, and use your real availability
                      when reviewing new commitments.
                    </p>
                  </div>
                </div>

                {!connected && (
                  <button onClick={() => connect(false, ['calendar'])} disabled={isConnecting} className="primary-button shrink-0 disabled:opacity-60">
                    {isConnecting ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
                    Enable Calendar
                  </button>
                )}
              </div>

              {connected && (
                <div className="grid gap-px border-t border-[#e2e7e3] bg-[#e2e7e3] sm:grid-cols-3">
                  {[
                    ['Last sync', syncStatus?.last_sync ? new Date(syncStatus.last_sync).toLocaleString() : 'Not available'],
                    ['Imported events', syncStatus?.import_stats?.events_from_google ?? 0],
                    ['Pending conflicts', conflicts.length],
                  ].map(([label, value]) => (
                    <div key={label} className="bg-[#fbfcfb] p-4">
                      <p className="text-[11px] font-bold uppercase text-[#7d8881]">{label}</p>
                      <p className="mt-2 text-sm font-semibold text-[#344139]">{value}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="surface-panel space-y-3 p-5" aria-label="Gmail mailbox permission">
              <h2 className="font-bold">Gmail mailbox controls</h2>
              <p className="text-sm text-[#68746d]">Archive or restore selected messages, change read status, manage stars and add or remove existing custom labels after reviewing each change in Mail. Google requires additional mailbox permission for these actions.</p>
              {connectionStatus?.mailbox_management ? <p className="text-sm font-semibold text-[#246b52]">Mailbox permission granted. Open a conversation in Mail to review a change.</p> : <button className="secondary-button" disabled={isConnecting} onClick={() => connect(true, ['gmail'])}>{isConnecting ? 'Connecting…' : 'Enable Gmail mailbox controls'}</button>}
            </section>

            {connected && (
              <section className="surface-panel overflow-hidden">
                <button
                  onClick={() => setShowAdvanced((current) => !current)}
                  className="flex w-full items-center justify-between px-5 py-4 text-left"
                >
                  <span>
                    <span className="block text-sm font-bold">Sync preferences</span>
                    <span className="mt-1 block text-xs text-[#7a857e]">Choose a calendar and which records to import or export.</span>
                  </span>
                  <ChevronDownIcon className={`h-5 w-5 text-[#6d7871] transition ${showAdvanced ? 'rotate-180' : ''}`} />
                </button>

                {showAdvanced && (
                  <div className="border-t border-[#e2e7e3] p-5">
                    <label htmlFor="sync-calendar" className="field-label">Calendar to sync</label>
                    <select id="sync-calendar" value={calendarId} onChange={event => setCalendarId(event.target.value)} disabled={syncPending} className="field-input max-w-md">
                      <option value="primary">Primary calendar</option>
                      {calendars.filter(calendar => calendar.id !== 'primary').map(calendar => <option key={calendar.id} value={calendar.id}>{calendar.name}</option>)}
                    </select>
                    <div className="mt-4 space-y-3 text-sm">
                      <label className="flex items-center gap-2"><input type="checkbox" checked={tasksConnected && includeNotes} onChange={event => setIncludeNotes(event.target.checked)} disabled={syncPending || !tasksConnected} />Import Google Tasks as notes</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={exportEvents} onChange={event => setExportEvents(event.target.checked)} disabled={syncPending} />Export new local events to this calendar</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={tasksConnected && exportNotes} onChange={event => setExportNotes(event.target.checked)} disabled={syncPending || !includeNotes || !tasksConnected} />Export new pinned notes to my default Google Tasks list</label>
                      {!tasksConnected && <p className="text-xs text-[#996617]">Select Tasks in Google permissions to enable task imports and exports.</p>}
                    </div>
                    <p className="mt-3 max-w-xl text-xs leading-5 text-[#7a857e]">Sync imports connected records. Exports above create new records only. Existing Google meetings use reviewed calendar changes; differing note text keeps both versions for review. Google Tasks is separate from Google Keep.</p>
                  </div>
                )}
              </section>
            )}

            {(connected || tasksConnected) && conflicts.length > 0 && (
              <section className="surface-panel overflow-hidden">
                <div className="border-b border-[#e2e7e3] p-5">
                  <div className="status-pill status-pill-warning">{conflicts.length} conflicts</div>
                  <h2 className="mt-3 text-base font-bold">Changes need review</h2>
                </div>
                <div>
                  {conflicts.map((conflict) => (
                    <div key={`${conflict.type}-${conflict.id}`} className="flex flex-col gap-3 border-b border-[#e9edea] p-4 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold">{conflict.title || 'Calendar item changed in both places'}</p>
                        <p className="mt-1 text-xs text-[#7a857e]">{conflict.description || 'Choose which version should be kept.'}</p>
                        {conflict.type === 'note' && <div className="mt-3 grid gap-3 text-xs sm:grid-cols-2"><div><p className="font-bold">Timelyzer</p><p className="whitespace-pre-wrap">{conflict.local?.title}<br />{conflict.local?.content}</p></div><div><p className="font-bold">Google Tasks</p><p className="whitespace-pre-wrap">{conflict.google?.title}<br />{conflict.google?.content}</p></div></div>}
                      </div>
                      <div className="flex gap-2">
                        {conflict.type === 'event' ? <Link to={`/calendar/events/${conflict.id}`} className="primary-button min-h-9 px-3 text-xs">Review Google Calendar changes</Link> : <>
                          <button onClick={() => resolveConflict(conflict, 'keep_google')} className="secondary-button min-h-9 px-3 text-xs">Keep Google</button>
                          <button onClick={() => resolveConflict(conflict, 'keep_timelyzer')} className="primary-button min-h-9 px-3 text-xs">Keep Timelyzer</button>
                          <Link to={`/notes?note=${conflict.id}`} className="secondary-button min-h-9 px-3 text-xs">Edit Google Task text</Link>
                        </>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="space-y-4">
            <section className="surface-panel p-5">
              <h2 className="section-heading">Connection checklist</h2>
              <div className="mt-4 space-y-4">
                {[
                  ['Account authorization saved', accountSaved && !connectionStatus?.reconnect_required],
                  ['Calendar permission saved', connected],
                  ['Completed sync recorded', Boolean(syncStatus?.last_sync)],
                ].map(([label, complete]) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full ${complete ? 'bg-[#dcece4] text-[#246b52]' : 'bg-[#edf0ed] text-[#8a948e]'}`}>
                      {complete ? <CheckCircleIcon className="h-4 w-4" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                    </span>
                    <span className="text-sm font-semibold text-[#4f5b54]">{label}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="surface-panel bg-[#f5f8f6] p-5">
              <ExclamationTriangleIcon className="h-5 w-5 text-[#996617]" />
              <h2 className="mt-3 text-sm font-bold">Review calendar access</h2>
              <p className="mt-2 text-xs leading-5 text-[#6c7770]">
                Connected calendars can contain sensitive event information. Disconnect the integration when you no
                longer want Timelyzer to synchronize it.
              </p>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default GoogleIntegration;
